/* eslint-disable jsx-a11y/label-has-associated-control */
import {
	useEffect, useMemo, useState
} from 'react';
import { connect } from 'react-redux';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	Tooltip,
} from '@mui/material';
import {
	Pencil, Plus, RefreshCcw, Trash2
} from 'lucide-react';
import SelectPicker from 'rsuite/SelectPicker';
import BackdropLoader from '../../../Components/Loaders/BackdropLoader';
import { AlertModal } from '../../../Components/Modals';
import { formActions, modalActions } from '../../../Actions';
import useFilteredStores from '../../../Helpers/useFilteredStores';
import { notificationServices } from '../../../Services';
import productionPlanAutoGenServices from '../../../Services/production-plan-autogen.services';

const DATE_OFFSETS = [
	{
		value: 'TODAY', label: 'Current Date',
	},
	{
		value: 'TOMORROW', label: 'Next Date',
	},
];

const TIME_SLOTS = (() => {
	const slots = [];
	for (let h = 0; h < 24; h += 1) {
		for (let m = 0; m < 60; m += 30) {
			slots.push(
				`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
			);
		}
	}
	return slots;
})();

// Filters the destination stores whose orders get pulled into the plan. ANY is
// sent as null — the backend then applies no delivery-type filter at all.
const DELIVERY_TYPES = [
	{
		value: 'ANY', label: 'Any',
	},
	{
		value: 'LOCAL', label: 'Local',
	},
	{
		value: 'OUTSTATION', label: 'Outstation',
	},
];

const SHORT_INFO_MAX_LENGTH = 20;

const EMPTY_FORM = {
	id: null,
	storeId: '',
	shiftId: '',
	planDateOffset: 'TOMORROW',
	ordersDateOffset: 'TOMORROW',
	deliveryType: 'ANY',
	generationTime: '06:00',
	shortInfo: '',
	active: true,
};

const offsetLabel = (val) => {
	return DATE_OFFSETS.find((o) => {
		return o.value === val;
	})?.label || val;
};

const deliveryTypeLabel = (val) => {
	return DELIVERY_TYPES.find((o) => {
		return o.value === (val || 'ANY');
	})?.label || val;
};

const AutoGenConfig = ({ stores, fetchStores, toggleAlert }) => {
	const [configs, setConfigs] = useState([]);
	const [shifts, setShifts] = useState([]);
	const [isLoading, setLoading] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState(EMPTY_FORM);

	const filteredStores = useMemo(() => {
		return useFilteredStores();
	}, [stores]);

	const fetchConfigs = async () => {
		try {
			setLoading(true);
			const res = await productionPlanAutoGenServices.list({
				data: {
					includeInactive: false,
				},
			});
			if (res?.statusCode === 200) {
				setConfigs(res?.data || []);
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const fetchShiftsForStore = async (storeId) => {
		if (!storeId) {
			setShifts([]);
			return;
		}
		try {
			const res = await productionPlanAutoGenServices.listShiftsForStore(storeId);
			if (res?.statusCode === 200) {
				setShifts(res?.data || []);
			} else {
				setShifts([]);
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error(err);
			setShifts([]);
		}
	};

	useEffect(() => {
		fetchStores({
			entity: 'STORES',
			req: {
				params: {
					id: '',
				},
			},
			method: 'LIST',
		});
		fetchConfigs();
	}, []);

	useEffect(() => {
		if (dialogOpen && form.storeId) {
			fetchShiftsForStore(form.storeId);
		}
	}, [dialogOpen, form.storeId]);

	const handleOpenCreate = () => {
		setForm(EMPTY_FORM);
		setShifts([]);
		setDialogOpen(true);
	};

	const handleOpenEdit = (cfg) => {
		setForm({
			id: cfg.id,
			storeId: cfg.storeId,
			shiftId: cfg.shiftId,
			planDateOffset: cfg.planDateOffset,
			ordersDateOffset: cfg.ordersDateOffset,
			deliveryType: cfg.deliveryType || 'ANY',
			generationTime: cfg.generationTime,
			shortInfo: cfg.shortInfo || '',
			active: cfg.active,
		});
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setForm(EMPTY_FORM);
		setShifts([]);
	};

	const handleFormChange = (key, value) => {
		setForm((prev) => {
			const next = {
				...prev, [key]: value,
			};
			if (key === 'storeId' && value !== prev.storeId) {
				next.shiftId = '';
			}
			return next;
		});
	};

	const handleSave = async () => {
		if (!form.storeId || !form.shiftId || !form.generationTime) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Store, shift, and generation time are required',
			});
			return;
		}
		const trimmedShortInfo = (form.shortInfo || '').trim();
		if (trimmedShortInfo.length > SHORT_INFO_MAX_LENGTH) {
			notificationServices.generateNotification({
				type: 'error',
				message: `Short info must be at most ${SHORT_INFO_MAX_LENGTH} characters`,
			});
			return;
		}
		try {
			setLoading(true);
			const res = await productionPlanAutoGenServices.save({
				data: {
					id: form.id || undefined,
					storeId: form.storeId,
					shiftId: form.shiftId,
					planDateOffset: form.planDateOffset,
					ordersDateOffset: form.ordersDateOffset,
					deliveryType:
						form.deliveryType === 'ANY' ? null : form.deliveryType,
					generationTime: form.generationTime,
					shortInfo: trimmedShortInfo || null,
					active: form.active,
				},
			});
			if (res?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: form.id ? 'Config updated' : 'Config created',
				});
				handleCloseDialog();
				fetchConfigs();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Save failed',
				});
			}
		} catch (err) {
			notificationServices.generateNotification({
				type: 'error',
				message: err?.message || 'Save failed',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (cfg) => {
		toggleAlert(true, {
			heading: 'Delete configuration?',
			message: `Delete auto-generation config for ${cfg.storeName} / ${cfg.shiftName}?`,
			cancelText: 'No',
			confirmText: 'Yes',
			colorReverse: false,
			handleConfirm: async () => {
				toggleAlert(false);
				try {
					setLoading(true);
					const res = await productionPlanAutoGenServices.deleteConfig({
						data: {
							id: cfg.id,
						},
					});
					if (res?.statusCode === 200) {
						notificationServices.generateNotification({
							type: 'success',
							message: 'Config deleted',
						});
						fetchConfigs();
					}
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error(err);
				} finally {
					setLoading(false);
				}
			},
		});
	};

	return (
		<div className='p-4'>
			<BackdropLoader isLoading={isLoading} showProgress />
			{/* AlertModal is mounted per-page, not globally — without this the
			delete confirmation dispatched by handleDelete never renders. */}
			<AlertModal />
			<div className='flex justify-between items-center mb-4'>
				<div>
					<h2 className='font-bold text-[18px]'>
						Production Plan Auto-Generation
					</h2>
					<p className='text-sm text-gray-600'>
						Schedule automatic production plan generation per store and shift.
					</p>
				</div>
				<div className='flex gap-2 items-center'>
					<Tooltip title='Refresh'>
						<IconButton onClick={fetchConfigs}>
							<RefreshCcw className='size-4' />
						</IconButton>
					</Tooltip>
					<Button
						onClick={handleOpenCreate}
						variant='contained'
						className='bg-yellow-400 hover:bg-yellow-500 text-black'
						startIcon={<Plus className='size-4' />}
					>
						Add Config
					</Button>
				</div>
			</div>

			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Store</TableCell>
							<TableCell>Shift</TableCell>
							<TableCell>Store Type</TableCell>
							<TableCell>Plan Date</TableCell>
							<TableCell>Orders Date</TableCell>
							<TableCell>Generation Time</TableCell>
							<TableCell>Short Info</TableCell>
							<TableCell>Active</TableCell>
							<TableCell>Last Run</TableCell>
							<TableCell align='right'>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{configs.length === 0 && (
							<TableRow>
								<TableCell colSpan={10} align='center'>
									No configurations yet.
								</TableCell>
							</TableRow>
						)}
						{configs.map((cfg) => {
							return (
								<TableRow key={cfg.id}>
									<TableCell>{cfg.storeName}</TableCell>
									<TableCell>{cfg.shiftName}</TableCell>
									<TableCell>
										{deliveryTypeLabel(cfg.deliveryType)}
									</TableCell>
									<TableCell>{offsetLabel(cfg.planDateOffset)}</TableCell>
									<TableCell>{offsetLabel(cfg.ordersDateOffset)}</TableCell>
									<TableCell>{cfg.generationTime}</TableCell>
									<TableCell>{cfg.shortInfo || '—'}</TableCell>
									<TableCell>{cfg.active ? 'Yes' : 'No'}</TableCell>
									<TableCell>
										{cfg.lastRunAt
											? `${cfg.lastRunAt} (${cfg.lastRunStatus || '-'})`
											: '—'}
									</TableCell>
									<TableCell align='right'>
										<Tooltip title='Edit'>
											<IconButton onClick={() => { return handleOpenEdit(cfg); }}>
												<Pencil className='size-4' />
											</IconButton>
										</Tooltip>
										<Tooltip title='Delete'>
											<IconButton onClick={() => { return handleDelete(cfg); }}>
												<Trash2 className='size-4 text-red-600' />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				fullWidth
				maxWidth='sm'
				disableEnforceFocus
				disableRestoreFocus
			>
				<DialogTitle>
					{form.id ? 'Edit Auto-Generation Config' : 'Add Auto-Generation Config'}
				</DialogTitle>
				<DialogContent dividers>
					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Store *</label>
							<SelectPicker
								block
								data={filteredStores?.map((s) => {
									return {
										label: s.title || s.name,
										value: s.id,
										type: s.type || 'STORE',
									};
								})}
								groupBy='type'
								placeholder='Select a store'
								value={form.storeId || null}
								onChange={(value) => { return handleFormChange('storeId', value || ''); }}
								cleanable={false}
								menuStyle={{
									zIndex: 1400,
								}}
							/>
						</div>

						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Shift *</label>
							<SelectPicker
								block
								data={shifts?.map((sh) => {
									return {
										label: sh.shiftName,
										value: sh.id,
									};
								})}
								placeholder={form.storeId ? 'Select a shift' : 'Select a store first'}
								value={form.shiftId || null}
								onChange={(value) => { return handleFormChange('shiftId', value || ''); }}
								disabled={!form.storeId}
								cleanable={false}
								menuStyle={{
									zIndex: 1400,
								}}
							/>
						</div>

						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Store Type *</label>
							<RadioGroup
								row
								value={form.deliveryType}
								onChange={(e) => { return handleFormChange('deliveryType', e.target.value); }}
							>
								{DELIVERY_TYPES.map((opt) => {
									return (
										<FormControlLabel
											key={opt.value}
											value={opt.value}
											control={<Radio />}
											label={opt.label}
										/>
									);
								})}
							</RadioGroup>
							<span className='text-xs text-gray-500'>
								Limits which destination stores&apos; orders are pulled into
								the plan.
							</span>
						</div>

						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Plan Date *</label>
							<RadioGroup
								row
								value={form.planDateOffset}
								onChange={(e) => { return handleFormChange('planDateOffset', e.target.value); }}
							>
								{DATE_OFFSETS.map((opt) => {
									return (
										<FormControlLabel
											key={opt.value}
											value={opt.value}
											control={<Radio />}
											label={opt.label}
										/>
									);
								})}
							</RadioGroup>
						</div>

						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Orders Date *</label>
							<RadioGroup
								row
								value={form.ordersDateOffset}
								onChange={(e) => { return handleFormChange('ordersDateOffset', e.target.value); }}
							>
								{DATE_OFFSETS.map((opt) => {
									return (
										<FormControlLabel
											key={opt.value}
											value={opt.value}
											control={<Radio />}
											label={opt.label}
										/>
									);
								})}
							</RadioGroup>
						</div>

						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Generation Time *</label>
							<FormControl fullWidth size='small'>
								<Select
									value={form.generationTime}
									onChange={(e) => { return handleFormChange('generationTime', e.target.value); }}
								>
									{TIME_SLOTS.map((slot) => {
										return (
											<MenuItem key={slot} value={slot}>
												{slot}
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</div>

						<div className='flex flex-col gap-1'>
							<label className='text-sm font-medium'>Short Info</label>
							<TextField
								size='small'
								fullWidth
								value={form.shortInfo}
								onChange={(e) => {
									return handleFormChange(
										'shortInfo',
										e.target.value.slice(0, SHORT_INFO_MAX_LENGTH)
									);
								}}
								placeholder='Optional label appended to plan description'
								inputProps={{
									maxLength: SHORT_INFO_MAX_LENGTH,
								}}
								helperText={`${(form.shortInfo || '').length}/${SHORT_INFO_MAX_LENGTH}`}
							/>
						</div>

						<div className='flex items-center gap-2'>
							<label className='text-sm font-medium'>Active</label>
							<Switch
								checked={!!form.active}
								onChange={(e) => { return handleFormChange('active', e.target.checked); }}
							/>
						</div>
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Cancel</Button>
					<Button onClick={handleSave} variant='contained' color='primary'>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		stores: state.form.STORES.data.LIST,
	};
};

const mapDispatchToProps = {
	fetchStores: formActions.gateway,
	toggleAlert: modalActions.toggleAlert,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutoGenConfig);
